/**
 * prazoEngine.js — Motor Canônico Puro para Análise de Prazos e Previsão de Atraso da Oficina
 */
import {
  getBusinessDaysDiff,
  getBusinessDaysBetween,
  addBusinessDays,
  normalizeToNoon,
} from "./formatters";

/**
 * Status que representam paradas de retrabalho ou desvio no fluxo produtivo
 */
export const STATUS_DESVIOS = [
  "VERIFICAR",
  "FALHA_BANCA",
  "MANUTENCAO",
  "REVISAO",
  "PEDIDO_MODIFICADO",
];

/**
 * Etapas canônicas presentes na matriz histórica de tempos (Etapa x Modelo)
 */
export const ETAPAS_CANONICAS = [
  "SOLICITACAO",
  "MODELAGEM",
  "IMPRIMINDO",
  "CURA",
  "FUNDICAO",
  "BANCA",
  "POLIMENTO",
  "RESINA_FINALIZACAO",
];

/**
 * Mapeia o status do Kanban/pedido para a etapa de referência na matriz de tempos,
 * aplicando as regras de desvios, interceptados e equivalências.
 *
 * @param {string} statusAtual - O status atual do pedido no Kanban
 * @param {Object} options - Parâmetros adicionais { modelo, isIntercepted }
 * @returns {Object} { etapaMapeada, emDesvio, isInterceptado, isFinalizado }
 */
export function mapStatusToEtapa(
  statusAtual,
  { modelo = null, isIntercepted = false } = {}
) {
  const rawStatus = String(statusAtual || "").trim().toUpperCase();
  const normStatus = rawStatus.replace(/\s+/g, "_");
  const isEscapulario = String(modelo || "")
    .toLowerCase()
    .includes("escapulario");

  const isDesvio = STATUS_DESVIOS.includes(normStatus);

  const isPronto = normStatus === "PEDIDO_PRONTO" || normStatus === "PRONTO";
  const isEnviado = normStatus === "ENVIADO" || normStatus === "ENTREGUE" || normStatus === "EXPEDIDO";
  const isCancelado = normStatus === "CANCELADO";
  const isFinalizado = isPronto || isEnviado || isCancelado;

  // Status de finalização
  if (isFinalizado) {
    return {
      etapaMapeada: null,
      emDesvio: false,
      isInterceptado: false,
      isFinalizado: true,
      isPronto,
      isEnviado,
      isCancelado,
    };
  }

  // Peças interceptadas
  const isInterceptadoStatus =
    normStatus.startsWith("INTERCEPTADO_") || Boolean(isIntercepted);

  if (isInterceptadoStatus) {
    if (isEscapulario) {
      // Escapulários pré-fabricados interceptados entram no ciclo fabril em BANCA
      return {
        etapaMapeada: "BANCA",
        emDesvio: false,
        isInterceptado: true,
        isFinalizado: false,
      };
    }
    // Demais modelos interceptados não percorrem a manufatura fabril do zero
    return {
      etapaMapeada: null,
      emDesvio: false,
      isInterceptado: true,
      isFinalizado: true,
    };
  }

  // Mapeamentos diretos de equivalência
  let etapaMapeada = normStatus;

  if (normStatus === "IMPRIMIR") {
    // 2.A: Imprimir assume o tempo de Imprimindo
    etapaMapeada = "IMPRIMINDO";
  } else if (
    normStatus === "FALHA_BANCA" ||
    normStatus === "MANUTENCAO" ||
    normStatus === "GRAVACAO"
  ) {
    etapaMapeada = "BANCA";
  } else if (
    normStatus === "VERIFICAR"
  ) {
    // "Já o verificar por exemplo, esse sim reseta o prazo e joga automaticamente o prazo para aguardando analise"
    etapaMapeada = "SOLICITACAO";
  } else if (
    normStatus === "REVISAO" ||
    normStatus === "PEDIDO_MODIFICADO"
  ) {
    etapaMapeada = "SOLICITACAO";
  } else if (
    normStatus === "ACABAMENTO_EXTERNO" ||
    normStatus === "BANHO"
  ) {
    etapaMapeada = "POLIMENTO";
  } else if (normStatus === "INJETADO") {
    // Injetado é a etapa de cera física pronta aguardando montagem de árvore (equivalente temporal à Cura da resina)
    etapaMapeada = "CURA";
  }

  const isRevisao = normStatus === "REVISAO" || normStatus === "PEDIDO_MODIFICADO";
  const isVerificar = normStatus === "VERIFICAR";

  return {
    etapaMapeada,
    emDesvio: isDesvio,
    isInterceptado: false,
    isFinalizado: false,
    isPronto,
    isEnviado,
    isCancelado,
    isRevisao,
    isVerificar,
  };
}

export const DIAS_SEMANA = [
  "Domingo",
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
];

export const DIAS_SEMANA_ABREV = [
  "Dom",
  "Seg",
  "Ter",
  "Qua",
  "Qui",
  "Sex",
  "Sáb",
];

/**
 * Formata uma data incluindo o dia da semana (ex: "15/09 (Segunda-feira)")
 */
export function formatDataComDiaSemana(dateInput, abrev = false) {
  if (!dateInput) return "-";
  try {
    const rawDate = normalizeToNoon(dateInput);
    if (!rawDate || isNaN(rawDate.getTime())) return "-";

    const dia = String(rawDate.getDate()).padStart(2, "0");
    const mes = String(rawDate.getMonth() + 1).padStart(2, "0");
    const lista = abrev ? DIAS_SEMANA_ABREV : DIAS_SEMANA;
    const diaSemana = lista[rawDate.getDay()];

    return `${dia}/${mes} (${diaSemana})`;
  } catch {
    return "-";
  }
}

/**
 * Arredonda para no máximo 1 casa decimal evitando dízimas e imprecisões IEEE 754 (ex: 0.15000000000000036 -> 0.2)
 */
export const round1 = (val) => {
  if (val === null || val === undefined) return 0;
  const num = Number(val);
  if (isNaN(num)) return 0;
  return Number(num.toFixed(1));
};

/**
 * Formata dias úteis de forma limpa sem casas decimais desnecessárias (ex: 2.0 -> "2", 0.1500000036 -> "0.2")
 */
export function formatDiasUteis(dias) {
  if (dias === null || dias === undefined) return "-";
  const num = round1(dias);
  if (Math.abs(num - Math.round(num)) < 0.01) {
    return String(Math.round(num));
  }
  return String(num);
}

/**
 * Função pura central para cálculo e análise do prazo de um pedido.
 *
 * @param {Object} params
 * @param {Date|string|Object} params.dataCompra - Data do pedido/compra (marco zero)
 * @param {string} params.statusAtual - Status atual da produção
 * @param {number} params.prazoPrometido - Prazo prometido ao cliente na venda (dias úteis)
 * @param {Object} params.temposEtapa - Dicionário etapa -> dias úteis restantes (ou mapa completo por modelo)
 * @param {string} [params.modelo] - Nome do modelo (opcional, para resolução no mapa completo ou regras específicas)
 * @param {boolean} [params.isIntercepted] - Flag de peça interceptada
 * @param {number} [params.fatorRisco] - Taxa histórica de retrabalho do modelo (0 a 1)
 * @param {Date} [params.hoje] - Data de referência (default: new Date())
 * @returns {Object} Análise temporal completa
 */
export function analisarPrazo({
  dataCompra,
  statusAtual,
  prazoPrometido,
  temposEtapa = {},
  modelo = null,
  isIntercepted = false,
  fatorRisco = 0,
  hoje = new Date(),
}) {
  const decorrido = round1(getBusinessDaysDiff(dataCompra, hoje));
  const prometido = Number(prazoPrometido) || 0;

  const {
    etapaMapeada,
    emDesvio,
    isInterceptado,
    isFinalizado,
    isPronto,
    isEnviado,
    isCancelado,
    isRevisao,
    isVerificar,
  } = mapStatusToEtapa(statusAtual, { modelo, isIntercepted });

  let faltam = 0;
  if (!isFinalizado && etapaMapeada) {
    // Permite temposEtapa direto { BANCA: 2.1 } ou estruturado por modelo { Triunfo: { BANCA: 2.1 } }
    const mapaEtapas =
      modelo && temposEtapa[modelo] ? temposEtapa[modelo] : temposEtapa;
    faltam = round1(Number(mapaEtapas[etapaMapeada]) || 0);
  }

  const previstoDias = round1(decorrido + faltam);
  const dataPrevista =
    faltam > 0 ? addBusinessDays(hoje, Math.ceil(faltam)) : hoje;

  // Data final prometida na venda
  const dataPrometida =
    prometido > 0 && dataCompra ? addBusinessDays(dataCompra, prometido) : null;

  let folga = prometido > 0 ? round1(prometido - previstoDias) : 0;

  let status = "NO_PRAZO";
  let atrasoEsperado = 0;
  let nivelRisco = "BAIXO"; // BAIXO | MODERADO | MEDIO | ALTO | MUITO_ALTO | CRITICO | CONCLUIDO | DESCONHECIDO
  let descricaoRisco = "";

  if (isCancelado) {
    status = "CANCELADO";
    nivelRisco = "DESCONHECIDO";
    descricaoRisco = "Pedido cancelado.";
    folga = 0;
    atrasoEsperado = 0;
  } else if (isPronto || isEnviado) {
    status = isPronto ? "PRONTO" : "ENVIADO";
    nivelRisco = "CONCLUIDO";
    atrasoEsperado = 0;
    faltam = 0;
    folga = prometido > 0 ? Math.max(0, round1(prometido - decorrido)) : 0;
    descricaoRisco = isPronto
      ? "Peça pronta na oficina! Produção fabril finalizada com sucesso — não há mais risco de atraso fabril, depende apenas do envio (logística)."
      : "Pedido finalizado e despachado/entregue.";
  } else if (isRevisao) {
    status = "EM_REVISAO";
    nivelRisco = "REVISAO";
    atrasoEsperado = 0;
    descricaoRisco = "Pedido em revisão. O andamento da produção depende da análise desta revisão (o próximo status definirá a continuidade ou retrabalho do prazo).";
  } else if (prometido <= 0) {
    status = "SEM_PRAZO";
    nivelRisco = "DESCONHECIDO";
    descricaoRisco = "Modelo sem prazo prometido configurado no sistema.";
  } else if (decorrido > prometido) {
    status = "ATRASADO";
    atrasoEsperado = round1(decorrido - prometido);
    nivelRisco = "CRITICO";
    descricaoRisco = isVerificar
      ? `Atrasado: prazo de produção resetado em Verificação para Aguardando Análise, extrapolando o prazo de venda em ${formatDiasUteis(atrasoEsperado)} dia(s) útil(eis).`
      : `Atrasado de verdade: já extrapolou o prazo de venda em ${formatDiasUteis(atrasoEsperado)} dia(s) útil(eis).`;
  } else if (previstoDias > prometido) {
    status = "VAI_ATRASAR";
    atrasoEsperado = round1(previstoDias - prometido);
    nivelRisco = "MUITO_ALTO";
    descricaoRisco = isVerificar
      ? `Em verificação: prazo de produção resetado para Aguardando Análise, com atraso provável de ~${formatDiasUteis(atrasoEsperado)} dia(s) útil(eis) além do prometido.`
      : `Estimativa dinâmica indica atraso provável de ~${formatDiasUteis(atrasoEsperado)} dia(s) útil(eis) além do prometido.`;
  } else if (folga <= 1) {
    status = "APERTADO";
    atrasoEsperado = 0;
    nivelRisco = "ALTO";
    descricaoRisco = `Folga crítica de apenas ${formatDiasUteis(folga)} dia(s) útil(eis). Qualquer retrabalho na bancada causará atraso.`;
  } else {
    status = "NO_PRAZO";
    atrasoEsperado = 0;
    if (isVerificar) {
      nivelRisco = "MEDIO";
      descricaoRisco = `Peça em verificação: prazo de produção resetado para Aguardando Análise (folga restante estimada: ${formatDiasUteis(folga)} d.ú.).`;
    } else if (emDesvio) {
      nivelRisco = "MEDIO";
      descricaoRisco = "Dentro do prazo, mas atualmente retido em fluxo de retrabalho/desvio.";
    } else if (Number(fatorRisco) >= 0.5) {
      nivelRisco = "MODERADO";
      descricaoRisco = `No prazo com ${formatDiasUteis(folga)} dia(s) de folga, mas o modelo tem histórico de ${Math.round(fatorRisco * 100)}% de retrabalho.`;
    } else {
      nivelRisco = "BAIXO";
      descricaoRisco = `Produção fluindo dentro do cronograma com folga de ${formatDiasUteis(folga)} dia(s) útil(eis).`;
    }
  }

  return {
    decorrido,
    faltam,
    previstoDias,
    dataPrevista,
    dataPrometida,
    dataPrevistaFormatada: isPronto ? "Pronto" : isEnviado ? "Enviado" : formatDataComDiaSemana(dataPrevista),
    dataPrometidaFormatada: formatDataComDiaSemana(dataPrometida),
    status,
    folga,
    atrasoEsperado,
    nivelRisco,
    descricaoRisco,
    emDesvio,
    isInterceptado,
    isFinalizado: Boolean(isFinalizado),
    isPronto: Boolean(isPronto),
    isEnviado: Boolean(isEnviado),
    isRevisao: Boolean(isRevisao),
    isVerificar: Boolean(isVerificar),
    etapaMapeada,
    prazoPrometido: prometido,
    fatorRisco: Number(fatorRisco) || 0,
  };
}

/**
 * Prioridade de gravidade dos status para ordenação e seleção do status do pedido completo:
 * ATRASADO (mais crítico) > VAI_ATRASAR > EM_REVISAO > APERTADO > NO_PRAZO > PRONTO > ENVIADO > SEM_PRAZO > CANCELADO
 */
export const STATUS_PRIORITY = {
  ATRASADO: 1,
  VAI_ATRASAR: 2,
  EM_REVISAO: 3,
  APERTADO: 4,
  NO_PRAZO: 5,
  PRONTO: 6,
  ENVIADO: 7,
  SEM_PRAZO: 8,
  CANCELADO: 9,
};

/**
 * Retorna o status mais crítico entre uma lista de status
 */
export function getMostCriticalStatus(statuses = []) {
  if (!statuses || statuses.length === 0) return "SEM_PRAZO";
  return [...statuses].sort(
    (a, b) => (STATUS_PRIORITY[a] || 99) - (STATUS_PRIORITY[b] || 99)
  )[0];
}
